<template>
  <div class="ies4-preview">
    <!-- 空気環境調査概要 -->
    <h5 class="section-title">空気環境調査概要</h5>
    <table class="table table-bordered summary-head-table mb-3">
      <tbody>
        <tr>
          <th>ビル名</th>
          <td>{{ buildingName }}</td>
          <th>測定年月日</th>
          <td>{{ formatDate(reportParams.measure_dt) }}</td>
        </tr>
        <tr>
          <th>実施者</th>
          <td>{{ reportParams.measure_person || '-' }}</td>
          <th>補助者</th>
          <td>{{ reportParams.assistant || '-' }}</td>
        </tr>
        <tr>
          <th>天候</th>
          <td>{{ reportParams.weather || '-' }}</td>
          <th>測定ポイント数</th>
          <td>{{ reportParams.area_point }} ポイント</td>
        </tr>
      </tbody>
    </table>

    <table v-if="measureResult" class="table table-bordered summary-table mb-4">
      <thead class="text-center">
        <tr>
          <th colspan="3">建築物環境衛生管理基準</th>
          <th colspan="2">測定値</th>
          <th>不適箇所数／測定箇所数</th>
          <th>概評</th>
        </tr>
        <tr>
          <th colspan="2">測定項目</th>
          <th>基準値</th>
          <th>最高値</th>
          <th>最低値</th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in summaryRows" :key="row.key">
          <td v-if="index === 0" class="group-cell" rowspan="3">温熱条件</td>
          <td v-else-if="index === 3" class="group-cell" rowspan="3">空気清浄度</td>
          <td>{{ row.label }}</td>
          <td>{{ row.std }}</td>
          <td>{{ formatValue(row.max, row.digits) }} {{ row.unit }}</td>
          <td>{{ formatValue(row.min, row.digits) }} {{ row.unit }}</td>
          <td>{{ row.bad }} ／ {{ row.all }}</td>
          <td class="comment-cell">{{ row.comment }}</td>
        </tr>
      </tbody>
    </table>
    <div v-else class="alert alert-warning">基準値マスタが不足しているため、概要を表示できません。</div>

    <!-- IES4の特記事項(Report/Description)は報告書の特記事項へ、取込元は社内向け備考へ登録される -->
    <table class="table table-bordered summary-head-table note-table mb-4">
      <tbody>
        <tr>
          <th>特記事項</th>
          <td class="note-cell">{{ reportParams.special_comment || '（なし）' }}</td>
        </tr>
        <tr>
          <th>社内向け備考</th>
          <td class="note-cell">{{ reportParams.internal_remarks || '（なし）' }}</td>
        </tr>
      </tbody>
    </table>

    <!-- 測定場所と測定機器 -->
    <div class="row g-3 mb-4">
      <div class="col-lg-6">
        <h5 class="section-title">測定場所（{{ workPlaces.length }}件）</h5>
        <table class="table table-bordered table-sm place-table">
          <thead class="text-center">
            <tr>
              <th class="no-col">No.</th>
              <th class="floor-col">階</th>
              <th>室名</th>
              <th class="outside-col">外気</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(workPlace, index) in workPlaces" :key="`place-${index}`">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ workPlace.floor_no }}</td>
              <td>{{ workPlace.work_place_nm }}</td>
              <td class="text-center">{{ workPlace.outside_air_flg === 1 ? '○' : '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="col-lg-6">
        <h5 class="section-title">測定機器</h5>
        <table class="table table-bordered table-sm device-table">
          <thead class="text-center">
            <tr>
              <th>測定項目</th>
              <th>測定機器</th>
              <th>製造者・型式・製造番号</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="device in usedDevices" :key="device.measure_type_key">
              <td>{{ device.measure_type_name }}</td>
              <td>
                {{ device.device_nm }}
                <span v-if="device.will_create" class="badge text-bg-warning ms-1">新規作成</span>
              </td>
              <td class="small">{{ device.detail }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 環境測定結果（報告書と同じ列構成） -->
    <h5 class="section-title">環境測定結果</h5>
    <div class="table-responsive mb-4">
      <table class="table result-table">
        <thead class="text-center">
          <tr>
            <th rowspan="4" class="work-place-col">測定場所</th>
            <th colspan="3">測定状況</th>
            <th colspan="2">温度</th>
            <th class="relative-humidity">相対湿度</th>
            <th class="air-current">気流</th>
            <th colspan="2">二酸化炭素</th>
            <th colspan="2">一酸化炭素</th>
            <th colspan="2">浮遊粉塵量</th>
            <th colspan="3">その他の事項</th>
            <th rowspan="4" class="remarks-col">備考</th>
          </tr>
          <tr>
            <th rowspan="2" class="time-col">時間</th>
            <th rowspan="2" class="person-col">在室<br>人数</th>
            <th rowspan="2" class="person-col">喫煙<br>者数</th>
            <th colspan="2">{{ stdRangeText('temperture', '℃') }}</th>
            <th class="relative-humidity">{{ stdRangeText('relative_humidity', '%') }}</th>
            <th rowspan="2" class="air-current">{{ stdMaxText('air_current', 'm/sec') }}</th>
            <th colspan="2">{{ stdMaxText('co2', 'ppm') }}</th>
            <th colspan="2">{{ stdMaxText('co', 'ppm') }}</th>
            <th colspan="2">{{ stdMaxText('float_powder_dust', 'mg/㎡') }}</th>
            <th rowspan="2" class="etc-col">照度</th>
            <th rowspan="2" class="etc-col">騒音</th>
            <th rowspan="2" class="etc-col">{{ otherMeasureType }}</th>
          </tr>
          <tr>
            <!-- IES4は乾球／湿球ではなく1.2m（TempU）と0.1m（TempL）の2高さで温度を測定する -->
            <th class="temp-col">1.2m</th>
            <th class="temp-col">0.1m</th>
            <th class="relative-humidity">120cm</th>
            <th class="value-col">瞬間値</th>
            <th class="value-col">平均値</th>
            <th class="value-col">瞬間値</th>
            <th class="value-col">平均値</th>
            <th class="value-col">瞬間値</th>
            <th class="value-col">平均値</th>
          </tr>
          <tr>
            <th class="time-col">時：分</th>
            <th class="person-col">人</th>
            <th class="person-col">人</th>
            <th class="temp-col">℃</th>
            <th class="temp-col">℃</th>
            <th class="relative-humidity">%</th>
            <th class="air-current">m/sec</th>
            <th class="value-col">ppm</th>
            <th class="value-col">ppm</th>
            <th class="value-col">ppm</th>
            <th class="value-col">ppm</th>
            <th class="value-col">mg/㎡</th>
            <th class="value-col">mg/㎡</th>
            <th class="etc-col">Lx</th>
            <th class="etc-col">dB</th>
            <th class="etc-col">{{ otherMeasureUnit }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(workPlace, wpIndex) in tableWorkPlaces" :key="`wp-${wpIndex}`">
            <tr
              v-for="(content, cIndex) in displayContents(workPlace)"
              :key="`wp-${wpIndex}-${cIndex}`"
              :class="{ 'place-last-row': cIndex === rowCount(workPlace) - 1 }"
            >
              <td v-if="cIndex === 0" class="work-place-col" :rowspan="rowCount(workPlace)">
                <div class="fw-bold">{{ workPlace.floor_no }}</div>
                <div>{{ workPlace.work_place_nm }}</div>
                <div v-if="workPlace.outside_air_flg === 1" class="text-muted outside-note">外気</div>
              </td>
              <td class="text-center">{{ formatTime(content?.measure_time) }}</td>
              <td class="text-end">{{ content?.room_people_num }}</td>
              <td class="text-end">{{ content?.smoker_no }}</td>
              <td class="text-end">{{ formatValue(content?.temperature_upper, 1) }}</td>
              <td class="text-end">{{ formatValue(content?.temperature_lower, 1) }}</td>
              <td class="text-end">{{ formatValue(content?.relative_humidity, 1) }}</td>
              <td class="text-end">{{ formatValue(content?.air_current, 2) }}</td>
              <td class="text-end">{{ formatValue(content?.co2_itt, 0) }}</td>
              <td v-if="cIndex === 0" class="text-end" :rowspan="rowCount(workPlace)">
                {{ formatValue(workPlace.co2_avg, 0) }}
              </td>
              <td class="text-end">{{ formatValue(content?.co_itt, 1) }}</td>
              <td v-if="cIndex === 0" class="text-end" :rowspan="rowCount(workPlace)">
                {{ formatValue(workPlace.co_avg, 1) }}
              </td>
              <td class="text-end">{{ formatValue(content?.float_powder_dust_itt, 3) }}</td>
              <td v-if="cIndex === 0" class="text-end" :rowspan="rowCount(workPlace)">
                {{ formatValue(workPlace.float_powder_dust_avg, 3) }}
              </td>
              <td class="text-end">{{ formatValue(content?.illuminance, 0) }}</td>
              <td class="text-end">{{ formatValue(content?.noise, 1) }}</td>
              <td class="etc-col"></td>
              <td class="remarks-col">{{ content?.remarks }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 測定結果グラフ（報告書と同じ2セクション構成） -->
    <div v-for="group in chartGroups" :key="group.key" class="chart-section mb-4">
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-2">
        <h5 class="section-title mb-0">{{ group.title }}</h5>
        <table class="table table-bordered symbol-table">
          <thead>
            <tr>
              <th>記号</th>
              <th>測定日</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div class="symbol-line">
                  <span v-for="round in roundCount" :key="`sym-${round}`" class="symbol-dot"></span>
                </div>
                <div class="symbol-caption">
                  <span v-for="round in roundCount" :key="`cap-${round}`">{{ round }}回目</span>
                </div>
              </td>
              <td class="text-center">{{ formatJapaneseEraDate(reportParams.measure_dt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-for="item in group.items" :key="item.key" class="chart-row">
        <div class="chart-vertical-label">{{ item.label }}</div>
        <div class="chart-indoor">
          <canvas :id="`ies4Chart_${item.key}`"></canvas>
        </div>
        <div class="chart-outside" :class="{ 'not-applicable-area': item.outsideNotApplicable }">
          <canvas :id="`ies4ChartOutside_${item.key}`"></canvas>
          <div v-if="item.outsideNotApplicable" class="not-applicable-overlay">測定対象外</div>
        </div>
      </div>

      <div class="outside-label-area">
        <div class="outside-label">外気</div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, watch } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// 1測定場所あたりのX軸の幅（報告書と同じく場所ごとに区切って表示する）
const SLOT_WIDTH = 3;
const LINE_COLOR = '#3d5788';
const BAR_FILL = '#abbbda';
const BAR_BORDER = '#677ca3';
const STD_COLOR = 'red';

// 温熱条件は瞬間値の折れ線、空気清浄度は瞬間値の折れ線＋平均値の棒（報告書と同じ表現）
const CHART_GROUPS = [
  {
    key: 'thermal',
    title: '温熱条件測定結果',
    items: [
      { key: 'temperture', label: '温度', unit: '℃', field: 'dry_bulb', digits: 1, hasBar: false },
      { key: 'relative_humidity', label: '相対湿度', unit: '%', field: 'relative_humidity', digits: 1, hasBar: false, fixedMax: 100 },
      { key: 'air_current', label: '気流', unit: 'm/秒', field: 'air_current', digits: 2, hasBar: false, outsideNotApplicable: true }
    ]
  },
  {
    key: 'cleanliness',
    title: '空気清浄度測定結果',
    items: [
      { key: 'co2', label: '二酸化炭素', unit: 'PPM', field: 'co2_itt', avgField: 'co2_avg', digits: 0, hasBar: true },
      { key: 'co', label: '一酸化炭素', unit: 'PPM', field: 'co_itt', avgField: 'co_avg', digits: 1, hasBar: true },
      { key: 'float_powder_dust', label: '浮遊粉塵量', unit: 'mg/㎡', field: 'float_powder_dust_itt', avgField: 'float_powder_dust_avg', digits: 3, hasBar: true }
    ]
  }
];

// 測定場所名をX軸の下に折り返して描画する
const placeLabelPlugin = {
  id: 'ies4PlaceLabel',
  afterDraw(chart, args, options) {
    const labels = options?.labels || [];
    if (!labels.length) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;

    ctx.save();
    ctx.font = 'bold 9px sans-serif';
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    labels.forEach((label, index) => {
      const x = xScale.getPixelForValue(index * SLOT_WIDTH + SLOT_WIDTH / 2);
      if (x < chartArea.left || x > chartArea.right) return;

      const lines = String(label).match(/.{1,5}/g) || [];
      lines.slice(0, 3).forEach((line, lineIndex) => {
        ctx.fillText(line, x, chartArea.bottom + 6 + lineIndex * 11);
      });
    });

    ctx.restore();
  }
};

// 基準値の赤い横線と、右余白への基準値ラベル
const stdLinePlugin = {
  id: 'ies4StdLine',
  afterDatasetsDraw(chart, args, options) {
    const lines = options?.lines || [];
    if (!lines.length) return;

    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    if (!yScale) return;

    ctx.save();
    lines.forEach((line) => {
      if (line.value === null || line.value === undefined) return;
      if (line.value < yScale.min || line.value > yScale.max) return;

      const y = yScale.getPixelForValue(line.value);

      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = STD_COLOR;
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();

      if (line.label) {
        ctx.font = '11px sans-serif';
        ctx.fillStyle = STD_COLOR;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(line.label, chartArea.right + 6, y);
      }
    });
    ctx.restore();
  }
};

export default {
  name: 'Ies4ReportPreview',
  props: {
    visible: { type: Boolean, default: false },
    buildingName: { type: String, default: '' },
    reportParams: { type: Object, default: () => ({}) },
    measureResult: { type: Object, default: null },
    usedDevices: { type: Array, default: () => [] }
  },
  setup(props) {
    let chartInstances = [];

    const workPlaces = computed(() => props.reportParams.work_places || []);
    const indoorWorkPlaces = computed(() => workPlaces.value.filter((place) => place.outside_air_flg !== 1));
    const outsideWorkPlaces = computed(() => workPlaces.value.filter((place) => place.outside_air_flg === 1));

    // 報告書詳細・帳票は室内を並べた後に外気を連結する（MeasurementReportDetail.vueのchunkArrayCustom）。
    // プレビューもファイル内の記載順ではなく、報告書と同じ室内→外気の順で表示する。
    const tableWorkPlaces = computed(() => indoorWorkPlaces.value.concat(outsideWorkPlaces.value));

    // IES4取込では「その他の事項」の任意項目は未設定のため空欄になる
    const otherMeasureType = computed(() => props.reportParams.other_measure_type || '');
    const otherMeasureUnit = computed(() => props.reportParams.other_measure_unit || '');

    const roundCount = computed(() => workPlaces.value.reduce(
      (max, place) => Math.max(max, (place.t_measurement_work_place_contents || []).length),
      0
    ));

    // 報告書詳細・帳票と同じく、明細は常に3行で表示する（不足分は空行）
    const CONTENT_DISPLAY_ROWS = 3;
    const displayContents = (workPlace) => {
      const rows = (workPlace?.t_measurement_work_place_contents || []).slice();
      while (rows.length < CONTENT_DISPLAY_ROWS) {
        rows.push(null);
      }
      return rows;
    };
    const rowCount = (workPlace) => displayContents(workPlace).length;

    const formatValue = (value, digits) => {
      if (value === null || value === undefined || value === '') return '';
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return '';
      return numberValue.toFixed(digits);
    };

    const formatDate = (value) => {
      if (!value) return '-';
      const [year, month, day] = String(value).split('-');
      return year && month && day ? `${year}年${Number(month)}月${Number(day)}日` : String(value);
    };

    // 報告書のグラフ凡例と同じ和暦表記（例: R8.5.13）
    const formatJapaneseEraDate = (value) => {
      if (!value) return '';
      const [year, month, day] = String(value).split('-').map(Number);
      if (!year || !month || !day) return String(value);
      return `R${year - 2018}.${month}.${day}`;
    };

    const formatTime = (value) => {
      if (!value) return '';
      const matched = String(value).match(/(\d{2}):(\d{2})/);
      return matched ? `${matched[1]}:${matched[2]}` : String(value);
    };

    const stdRangeText = (key, unit) => {
      if (!props.measureResult) return '';
      const min = props.measureResult[`${key}_min_std`];
      const max = props.measureResult[`${key}_max_std`];
      return min === undefined || min === null ? '' : `${min}〜${max} ${unit}`;
    };

    const stdMaxText = (key, unit) => {
      if (!props.measureResult) return '';
      const max = props.measureResult[`${key}_max_std`];
      return max === undefined || max === null ? '' : `${max}${unit}以下`;
    };

    const summaryRows = computed(() => {
      const result = props.measureResult;
      if (!result) return [];

      return [
        { key: 'temperture', label: '温度', unit: '℃', digits: 1, std: `${result.temperture_min_std} 〜 ${result.temperture_max_std} ℃` },
        { key: 'relative_humidity', label: '相対湿度', unit: '%', digits: 1, std: `${result.relative_humidity_min_std} 〜 ${result.relative_humidity_max_std} %` },
        { key: 'air_current', label: '気流', unit: 'm/sec', digits: 2, std: `${result.air_current_max_std} m/sec 以下` },
        { key: 'co2', label: '二酸化炭素(CO2)', unit: 'ppm', digits: 0, std: `${result.co2_max_std} ppm 以下` },
        { key: 'co', label: '一酸化炭素(CO)', unit: 'ppm', digits: 1, std: `${result.co_max_std} ppm 以下` },
        { key: 'float_powder_dust', label: '浮遊粉塵量', unit: 'mg/㎡', digits: 3, std: `${result.float_powder_dust_max_std} mg/㎡ 以下` }
      ].map((row) => ({
        ...row,
        max: result[`${row.key}_max_measure`],
        min: result[`${row.key}_min_measure`],
        bad: result[`${row.key}_inappropriate_points`],
        all: result[`${row.key}_all_points`],
        comment: result[`${row.key}_comment`]
      }));
    });

    const destroyCharts = () => {
      chartInstances.forEach((instance) => instance.destroy());
      chartInstances = [];
    };

    const stdLinesFor = (item) => {
      const result = props.measureResult;
      if (!result) return [];

      const lines = [];
      const min = result[`${item.key}_min_std`];
      const max = result[`${item.key}_max_std`];

      if (min !== undefined && min !== null) lines.push({ value: min, label: `${min}${item.unit}` });
      if (max !== undefined && max !== null) lines.push({ value: max, label: `${max}${item.unit}` });

      return lines;
    };

    // 測定場所ごとに区切ったX座標へ、測定回の値を並べる
    const buildChartData = (item, places) => {
      const lineData = [];
      const barData = [];

      places.forEach((place, placeIndex) => {
        const contents = place.t_measurement_work_place_contents || [];
        contents.forEach((content, roundIndex) => {
          const value = content[item.field];
          if (value === null || value === undefined) return;
          lineData.push({ x: placeIndex * SLOT_WIDTH + 0.5 + roundIndex, y: value });
        });

        if (item.hasBar) {
          const average = place[item.avgField];
          if (average !== null && average !== undefined) {
            barData.push({ x: placeIndex * SLOT_WIDTH + SLOT_WIDTH / 2, y: average });
          }
        }
      });

      const datasets = [];

      if (item.hasBar) {
        datasets.push({
          type: 'bar',
          label: '平均値',
          data: barData,
          backgroundColor: BAR_FILL,
          borderColor: BAR_BORDER,
          borderWidth: 1,
          barThickness: 12
        });
      }

      datasets.push({
        type: 'line',
        label: '瞬間値',
        data: lineData,
        borderColor: LINE_COLOR,
        backgroundColor: LINE_COLOR,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 3,
        // 測定場所をまたいで線がつながらないように区切る
        segment: {
          borderColor: (context) => (
            Math.floor(context.p0.parsed.x / SLOT_WIDTH) === Math.floor(context.p1.parsed.x / SLOT_WIDTH)
              ? LINE_COLOR
              : 'transparent'
          )
        }
      });

      return datasets;
    };

    const buildChartOptions = (item, places) => {
      const stdLines = stdLinesFor(item);
      const stdValues = stdLines.map((line) => line.value);

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { right: 64, bottom: 32 } },
        plugins: {
          legend: { display: false },
          ies4PlaceLabel: { labels: places.map((place) => place.work_place_nm) },
          ies4StdLine: { lines: stdLines },
          tooltip: {
            callbacks: {
              title: (contexts) => {
                const index = Math.floor(contexts[0].parsed.x / SLOT_WIDTH);
                return places[index] ? places[index].work_place_nm : '';
              },
              label: (context) => `${context.dataset.label}: ${formatValue(context.parsed.y, item.digits)} ${item.unit}`
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: 0,
            max: Math.max(places.length * SLOT_WIDTH, SLOT_WIDTH),
            ticks: { display: false },
            grid: { display: true }
          },
          y: {
            // 報告書と同じく0を基準にし、基準値が必ず収まる範囲にする
            beginAtZero: true,
            max: item.fixedMax,
            suggestedMax: stdValues.length ? Math.max(...stdValues) : undefined,
            title: {
              display: true,
              text: `(${item.unit})`,
              align: 'end',
              color: '#666',
              font: { size: 11, weight: 'normal' }
            },
            ticks: { font: { size: 10 } }
          }
        }
      };
    };

    const renderChart = (canvasId, item, places) => {
      const canvas = document.getElementById(canvasId);
      if (!canvas || !places.length) return;

      chartInstances.push(new Chart(canvas.getContext('2d'), {
        // 棒＋折れ線の混在グラフはベースの種別指定が必要
        type: item.hasBar ? 'bar' : 'line',
        data: { datasets: buildChartData(item, places) },
        plugins: [placeLabelPlugin, stdLinePlugin],
        options: buildChartOptions(item, places)
      }));
    };

    const renderCharts = () => {
      destroyCharts();

      CHART_GROUPS.forEach((group) => {
        group.items.forEach((item) => {
          renderChart(`ies4Chart_${item.key}`, item, indoorWorkPlaces.value);
          renderChart(`ies4ChartOutside_${item.key}`, item, outsideWorkPlaces.value);
        });
      });
    };

    // モーダル表示後でないとcanvasのサイズが確定しないため、表示されてから描画する
    watch(() => props.visible, async (visible) => {
      if (!visible) {
        destroyCharts();
        return;
      }
      await nextTick();
      renderCharts();
    });

    watch(() => props.reportParams, async () => {
      if (!props.visible) return;
      await nextTick();
      renderCharts();
    });

    onBeforeUnmount(destroyCharts);

    return {
      workPlaces,
      tableWorkPlaces,
      summaryRows,
      chartGroups: CHART_GROUPS,
      roundCount,
      rowCount,
      displayContents,
      otherMeasureType,
      otherMeasureUnit,
      formatValue,
      formatDate,
      formatJapaneseEraDate,
      formatTime,
      stdRangeText,
      stdMaxText
    };
  }
};
</script>

<style scoped>
.section-title {
  font-weight: 700;
  border-left: 4px solid #0d6efd;
  padding-left: 8px;
  margin-bottom: 8px;
}

.summary-head-table th {
  background: #f1f3f5;
  width: 130px;
  white-space: nowrap;
}

/* 報告書詳細の特記事項欄と同じく、IES4側の改行をそのまま表示する */
.note-table .note-cell {
  white-space: pre-line;
}

.summary-table th,
.summary-table td {
  font-size: 13px;
  vertical-align: middle;
}

.summary-table thead th {
  background: #f1f3f5;
  text-align: center;
}

.summary-table .group-cell {
  background: #f8f9fa;
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
  width: 90px;
}

.summary-table .comment-cell {
  font-size: 12px;
}

.place-table th,
.place-table td,
.device-table th,
.device-table td {
  font-size: 12px;
}

.place-table thead th,
.device-table thead th {
  background: #f1f3f5;
}

.place-table .no-col {
  width: 46px;
}

.place-table .floor-col {
  width: 76px;
}

.place-table .outside-col {
  width: 50px;
}

/* 環境測定結果テーブル（報告書詳細と同じ罫線・行高に合わせる） */
.result-table {
  font-size: 12px;
  white-space: nowrap;
  border: 3px solid #000;
  font-family: serif;
  margin-bottom: 0;
}

.result-table thead {
  border: 3px solid #000;
}

.ies4-preview .result-table th,
.ies4-preview .result-table td {
  padding: 2px 4px;
  font-weight: bold;
  border: 1px solid #000;
  vertical-align: middle;
  /* 測定値が入らない空行でも行の高さを保つ */
  height: 24px;
}

.ies4-preview .result-table thead th {
  text-align: center;
  background: #f1f3f5;
}

/* 測定場所の区切りを太線にする。
   測定場所名や平均値などの結合セルは1行目に属するため、tr側にも指定して
   結合セルの下端にも線が通るようにする（報告書詳細と同じ指定方法）。 */
.ies4-preview .result-table tr.place-last-row {
  border-bottom: 3px solid #000;
}

.ies4-preview .result-table tr.place-last-row td {
  border-bottom: 3px solid #000;
}

.ies4-preview .result-table .work-place-col {
  min-width: 150px;
  white-space: normal;
}

.ies4-preview .result-table .remarks-col {
  min-width: 180px;
  white-space: normal;
  text-align: left;
  font-weight: normal;
}

.result-table .outside-note {
  font-size: 10px;
  font-weight: normal;
}

.result-table .time-col {
  width: 52px;
}

.result-table .person-col {
  width: 42px;
}

.result-table .temp-col,
.result-table .value-col {
  width: 52px;
}

.result-table .relative-humidity {
  width: 62px;
}

.result-table .air-current {
  width: 62px;
}

.result-table .etc-col {
  width: 48px;
}


/* グラフ */
.chart-section {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
}

.symbol-table {
  width: 240px;
  font-size: 11px;
  margin-bottom: 0;
}

.symbol-table th {
  background: #f1f3f5;
  text-align: center;
  padding: 2px;
}

.symbol-table td {
  padding: 4px;
}

.symbol-line {
  display: flex;
  align-items: center;
  justify-content: space-around;
  border-top: 2px solid #3d5788;
  margin: 6px 4px 2px;
}

.symbol-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3d5788;
  margin-top: -4px;
}

.symbol-caption {
  display: flex;
  justify-content: space-around;
  font-size: 10px;
  color: #495057;
}

.chart-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-top: 12px;
}

.chart-vertical-label {
  writing-mode: vertical-rl;
  text-orientation: upright;
  letter-spacing: 4px;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex: 0 0 28px;
}

.chart-indoor {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 220px;
}

.chart-outside {
  position: relative;
  flex: 0 0 200px;
  height: 220px;
}

.not-applicable-area .not-applicable-overlay {
  position: absolute;
  inset: 20% 8% 30% 8%;
  background: #6c757d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.outside-label-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.outside-label {
  border: 1px solid #212529;
  padding: 6px 28px;
  font-weight: 700;
}
</style>
